/**
 * 外部数据源选择的会话级状态。
 *
 * 两组选择的记忆规则**故意不同**，因此拆成两个 store，而不是一个 store 里的两个
 * 字段——写在一起早晚会被顺手加上同一份持久化配置：
 *
 * - **租户级（全所共用凭据）**：沿用会话级记忆并持久化到 localStorage，与网页
 *   检索开关同规格。律师为某个会话选定"这轮要查工商登记"，刷新页面后仍然成立。
 * - **个人级（每人授权自己的账号）**：只存在内存里，**不写入任何持久化存储**。
 *   刷新、关标签页、重开应用一律回到全部关闭；新会话因为没有对应键，读出来就是
 *   空数组。个人账号里的数据（会议纪要、聊天记录）不该因为某次勾选就长期对本轮
 *   之外的提问生效，"上次开着这次也开着"在这里是风险而不是便利。
 *
 * 两个 store 都按会话 id 分键，切换会话不串味。首页 hero 尚无会话 id，那里的选择
 * 由页面自己的组件状态持有（同样不持久化），会话创建后再写入这两个 store。
 */

import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

/** 租户级选择的 localStorage 键名。 */
export const TENANT_DATA_SOURCE_STORAGE_KEY = "jerry-tenant-data-sources";

/** 读取缺省会话时返回的空数组，引用稳定，避免选择器每次返回新数组。 */
const EMPTY: string[] = [];

interface DataSourceSelectionState {
  /** conversationId → 已选数据源 key 列表。缺键 ⇒ 未选任何数据源。 */
  byConversation: Record<string, string[]>;
  /** 读取某会话的选择（缺省为空数组）。 */
  get: (conversationId: string) => string[];
  /** 勾选 / 取消勾选一项。 */
  toggle: (conversationId: string, key: string) => void;
  /** 整体覆盖某会话的选择（首页结转、按接入状态剪枝时使用）。 */
  set: (conversationId: string, keys: string[]) => void;
}

function createSelectionSlice(
  set: (fn: (s: DataSourceSelectionState) => Partial<DataSourceSelectionState>) => void,
  get: () => DataSourceSelectionState,
): DataSourceSelectionState {
  return {
    byConversation: {},
    get: (conversationId) => get().byConversation[conversationId] ?? EMPTY,
    toggle: (conversationId, key) =>
      set((s) => {
        const current = s.byConversation[conversationId] ?? EMPTY;
        const next = current.includes(key)
          ? current.filter((k) => k !== key)
          : [...current, key];
        return {
          byConversation: { ...s.byConversation, [conversationId]: next },
        };
      }),
    set: (conversationId, keys) =>
      set((s) => ({
        byConversation: { ...s.byConversation, [conversationId]: [...keys] },
      })),
  };
}

/** 租户级数据源选择——会话级记忆，持久化。 */
export const useTenantDataSourceStore = create<DataSourceSelectionState>()(
  persist((set, get) => createSelectionSlice(set, get), {
    name: TENANT_DATA_SOURCE_STORAGE_KEY,
    storage: createJSONStorage(() => localStorage),
  }),
);

/**
 * 个人级账号的本轮启用——只在内存里。
 *
 * 这里刻意不套 `persist`：一旦套上，"每次新会话一律关闭"就只剩注释在说，实际
 * 行为会变成跨会话跨天沿用。改动此处前先读本文件开头的理由。
 */
export const usePersonalDataSourceStore = create<DataSourceSelectionState>()(
  (set, get) => createSelectionSlice(set, get),
);
